import { Packer } from "docx";
import type { AnalysisDocument } from "@/api/analysis-api";
import { buildReportPrintDocument, printViaTempIframe } from "@/lib/report-pdf";
import { buildAnalysisDocx } from "./html-to-docx";

/**
 * Downloading an analysis as PDF or Word.
 *
 * Both build their own copy of the document rather than exporting the live
 * editor. The editor is full of chrome that has no business in a file sent to an
 * investment committee — regenerate buttons, status pills, the placeholder — and
 * rendering afresh means an export cannot be broken by whatever the editor
 * happens to be showing.
 */

export interface AnalysisExportOptions {
  /** The company the analysis is about. Goes in the header and the filename. */
  companyName?: string;
}

/** Section titles are chrome in the editor, so the export re-adds them. */
export const buildPrintableHtml = (analysis: AnalysisDocument): string =>
  analysis.sections
    .map((section) => {
      const body = (section.html ?? "").trim();
      const content = body
        ? body
        : section.status === "error"
          ? "<p><em>This section failed to generate.</em></p>"
          : "<p><em>This section has not been generated yet.</em></p>";

      return `<section class="mb-6"><h2>${escapeHtml(section.title)}</h2>${content}</section>`;
    })
    .join("");

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const exportAnalysisToPdf = async (
  analysis: AnalysisDocument,
  options: AnalysisExportOptions = {}
): Promise<void> => {
  const container = document.createElement("div");
  container.className = "prose prose-sm max-w-none";
  container.innerHTML = buildPrintableHtml(analysis);

  const html = buildReportPrintDocument(container, {
    reportTitle: analysis.template_name,
    companyName: options.companyName,
    generatedOn: analysis.updated_at,
  });

  await printViaTempIframe(html);
};

export const exportAnalysisToWord = async (
  analysis: AnalysisDocument,
  options: AnalysisExportOptions = {}
): Promise<void> => {
  const blob = await Packer.toBlob(
    buildAnalysisDocx(analysis, { subtitle: options.companyName })
  );

  downloadBlob(blob, `${analysisFileName(analysis, options)}.docx`);
};

export const analysisFileName = (
  analysis: AnalysisDocument,
  options: AnalysisExportOptions = {}
): string => {
  const parts = [options.companyName, analysis.template_name].filter(Boolean);
  // Characters Windows and macOS both refuse in filenames.
  return parts.join(" - ").replace(/[\\/:*?"<>|]/g, "").trim() || "Analysis";
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
